import { isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import {
  DatePicker as MUIDatePicker,
  DatePickerProps as MUIDatePickerProps,
} from "@mui/x-date-pickers/DatePicker";
import {
  DateTimePicker,
  DateTimePickerProps,
} from "@mui/x-date-pickers/DateTimePicker";

import { toSnakeCase } from "shared/utils";

type BaseDatePickerProps = Omit<
  MUIDatePickerProps,
  "value" | "onChange" | "label"
>;

export interface DatePickerProps extends BaseDatePickerProps {
  initialDate?: Date | null;
  defaultIsOpen?: boolean;
  onChange?: (date: Date | null) => void;
  placeholderText?: string;
  showTimeSelect?: boolean;
  isInsideFilterBuilder?: boolean;
  label?: React.ReactNode;
}

const DatePicker = ({
  initialDate = null,
  placeholderText,
  onChange,
  defaultIsOpen,
  autoFocus,
  showTimeSelect,
  isInsideFilterBuilder = false,
  label,
  ...otherProps
}: DatePickerProps) => {
  const { t } = useTranslation("common");

  const handleDateChange = (date: Date | null) => {
    // MUI sends an Invalid Date while a typed section is incomplete, for
    // example after a leading "0" in the day. Skip it so consumers only get
    // complete dates or null.
    if (date && !isValid(date)) return;

    onChange?.(date);
  };

  const inputId = label && typeof label === "string" ? toSnakeCase(label) : "";

  const commonProps = {
    ...otherProps,
    value: initialDate,
    onChange: handleDateChange,
    desktopModeMediaQuery: "@media (min-width: 0px)", // Always use desktop mode
    slotProps: {
      textField: {
        placeholder: placeholderText ?? t("date_picker.placeholder"),
        autoFocus: defaultIsOpen || autoFocus,
        id: inputId,
        error: false,
      },
    },
  };

  const datePicker = showTimeSelect ? (
    <DateTimePicker
      {...(commonProps as DateTimePickerProps)}
      className={isInsideFilterBuilder ? "w-56 mui-datepicker" : ""}
    />
  ) : (
    <MUIDatePicker
      {...(commonProps as MUIDatePickerProps)}
      className={isInsideFilterBuilder ? "w-40 mui-datepicker" : ""}
    />
  );

  if (label) {
    return (
      <FormControl>
        <FormLabel htmlFor={inputId}>{label}</FormLabel>
        {datePicker}
      </FormControl>
    );
  }

  return datePicker;
};

export default DatePicker;
